import React, { createContext, useContext, useEffect, useState } from 'react';
import { publicAPI, PublicAnimal, getImageUrl } from '../services/api';

interface AnimalsContextType {
  animals: PublicAnimal[];
  featuredAnimals: PublicAnimal[];
  loading: boolean;
  error: string | null;
  totalAnimals: number;
  currentPage: number;
  totalPages: number;
  pagination: any;
  stats: any;
  fetchAnimals: (params?: any) => Promise<void>;
  getAnimal: (id: string) => Promise<PublicAnimal | null>;
  refreshAnimals: () => Promise<void>;
  loadStats: () => Promise<void>;
}

const AnimalsContext = createContext<AnimalsContextType>({
  animals: [],
  featuredAnimals: [],
  loading: true,
  error: null,
  totalAnimals: 0,
  currentPage: 1,
  totalPages: 1,
  pagination: null,
  stats: null,
  fetchAnimals: async () => {},
  getAnimal: async () => null,
  refreshAnimals: async () => {},
  loadStats: async () => {},
});

export const useAnimals = () => {
  const context = useContext(AnimalsContext);
  if (!context) {
    throw new Error('useAnimals must be used within an AnimalsProvider');
  }
  return context;
};

interface AnimalsProviderProps {
  children: React.ReactNode;
}

export const AnimalsProvider: React.FC<AnimalsProviderProps> = ({ children }) => {
  const [animals, setAnimals] = useState<PublicAnimal[]>([]);
  const [featuredAnimals, setFeaturedAnimals] = useState<PublicAnimal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalAnimals, setTotalAnimals] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pagination, setPagination] = useState<any>(null);
  const [stats, setStats] = useState<any>(null);

  const fetchAnimals = async (params?: any) => {
    try {
      setLoading(true);
      setError(null);
      const response = await publicAPI.getAnimals(params);
      const fetchedAnimals = response.data.animals || [];
      const paginationData = response.data.pagination || {};
      
      // Update pagination state
      setTotalAnimals(paginationData.total || 0);
      setCurrentPage(paginationData.page || 1);
      setTotalPages(paginationData.totalPages || 1);
      setPagination(paginationData);
      
      // Transform backend data to match frontend interface
      const transformedAnimals: PublicAnimal[] = fetchedAnimals.map((animal: any) => ({
        id: animal.id,
        name: animal.name,
        gender: animal.gender === 'male' ? 'male' : 'female',
        age: animal.age,
        size: animal.size,
        color: animal.color,
        description: animal.description,
        image: getImageUrl(animal.id),
        status: 'available',
        vaccinated: Boolean(animal.vaccinated),
        neutered: Boolean(animal.neutered),
        chipped: Boolean(animal.chipped),
        entryDate: animal.entryDate,
        totalInvested: animal.totalInvested || 0,
      }));

      setAnimals(transformedAnimals);
      
      // Set featured animals (first 4)
      setFeaturedAnimals(transformedAnimals.slice(0, 4));
    } catch (err) {
      console.error('Error fetching animals:', err);
      setError('Failed to load animals');
      // Fallback to empty arrays
      setAnimals([]);
      setFeaturedAnimals([]);
    } finally {
      setLoading(false);
    }
  };

  const getAnimal = async (id: string): Promise<PublicAnimal | null> => {
    try {
      const response = await publicAPI.getAnimal(id);
      const animal = response.data;
      
      if (!animal) return null;
      
      // Transform backend data to match frontend interface
      return {
        id: animal.id,
        name: animal.name,
        gender: animal.gender === 'male' ? 'male' : 'female',
        age: animal.age,
        size: animal.size,
        color: animal.color,
        description: animal.description,
        image: getImageUrl(animal.id),
        status: 'available',
        vaccinated: Boolean(animal.vaccinated),
        neutered: Boolean(animal.neutered),
        chipped: Boolean(animal.chipped),
        entryDate: animal.entryDate,
        totalInvested: animal.totalInvested || 0,
      };
    } catch (err) {
      console.error('Error fetching animal:', err);
      return null;
    }
  };

  const refreshAnimals = async () => {
    await fetchAnimals({ limit: 12 });
  };

  const loadStats = async () => {
    try {
      const response = await publicAPI.getAnimalsStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  useEffect(() => {
    fetchAnimals({ limit: 12 }); // Initial load for Dogs page
  }, []);

    return (
    <AnimalsContext.Provider value={{ 
      animals, 
      featuredAnimals, 
      loading, 
      error,
      totalAnimals,
      currentPage,
      totalPages,
      pagination,
      stats,
      fetchAnimals, 
      getAnimal, 
      refreshAnimals,
      loadStats
    }}>
      {children}
    </AnimalsContext.Provider>
  );
}; 